import React, { useState, useEffect, useCallback } from 'react';
import { isAuthenticated, adminLogin } from '../../services/apiService';
import CSVImport from './CSVImport';
import PipedriveTimeline from './PipedriveTimeline';

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  bg:       '#F4F2EE',
  white:    '#FFFFFF',
  border:   '#E0DDD6',
  borderSm: '#ECEAE4',
  text:     '#1C1A17',
  muted:    '#7A7772',
  faint:    '#B5B2AC',
  brand:    '#3E4954',
  brandLt:  '#D0E5F5',
  accent:   '#C4330C',
  green:    '#166534',
  greenBg:  '#DCFCE7',
  red:      '#9F1239',
  redBg:    '#FFE4E6',
  blue:     '#1E40AF',
  blueBg:   '#DBEAFE',
  amber:    '#92400E',
  amberBg:  '#FEF3C7',
};

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Syne:wght@700;800&family=JetBrains+Mono:wght@400;500&display=swap');`;

const GLOBAL_CSS = `
${FONTS}
.crm * { box-sizing: border-box; margin: 0; padding: 0; }
.crm { font-family: 'Plus Jakarta Sans', sans-serif; background: ${T.bg}; min-height: 100vh; color: ${T.text}; }
.crm a { color: inherit; text-decoration: none; }
.mono { font-family: 'JetBrains Mono', monospace; }
.syne { font-family: 'Syne', sans-serif; }

.crm-table { width: 100%; border-collapse: collapse; }
.crm-table thead th {
  padding: 10px 14px; font-size: 10px; font-weight: 700; letter-spacing: 0.1em;
  text-transform: uppercase; color: ${T.muted}; text-align: left;
  border-bottom: 1px solid ${T.border}; background: ${T.bg};
  white-space: nowrap;
}
.crm-table tbody tr { border-bottom: 1px solid ${T.borderSm}; transition: background 80ms; cursor: pointer; }
.crm-table tbody tr:hover { background: rgba(62,73,84,0.035); }
.crm-table tbody tr.expanded { background: rgba(62,73,84,0.04); border-bottom: none; }
.crm-table tbody td { padding: 11px 14px; vertical-align: middle; }

.pill {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 7px; border-radius: 4px;
  font-size: 10px; font-weight: 600; letter-spacing: 0.03em;
  white-space: nowrap; font-family: 'JetBrains Mono', monospace;
}
.utm-tag {
  display: inline-flex; align-items: center; gap: 2px;
  padding: 1px 5px; border-radius: 3px; font-size: 9.5px;
  background: ${T.borderSm}; color: ${T.muted}; font-family: 'JetBrains Mono', monospace;
  white-space: nowrap; max-width: 120px; overflow: hidden; text-overflow: ellipsis;
  border: 1px solid ${T.border};
}
.utm-tag .key { color: ${T.faint}; }

.tg-tip {
  position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  background: ${T.text}; color: #fff; border-radius: 7px; padding: 8px 12px;
  font-size: 11px; white-space: nowrap; box-shadow: 0 4px 20px rgba(0,0,0,0.18);
  pointer-events: none; z-index: 50;
  font-family: 'Plus Jakarta Sans', sans-serif;
}
.tg-tip::after {
  content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
  border: 5px solid transparent; border-top-color: ${T.text};
}

.card { background: ${T.white}; border: 1px solid ${T.border}; border-radius: 12px; }
.card-inner { padding: 20px 24px; }

.funnel-node {
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 0;
}
.funnel-arrow {
  align-self: center; color: ${T.faint}; font-size: 18px; flex-shrink: 0; margin: 0 4px;
  padding-bottom: 22px;
}

.input-base {
  height: 34px; padding: 0 11px; border-radius: 7px;
  border: 1px solid ${T.border}; background: ${T.white};
  color: ${T.text}; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px;
  outline: none; transition: border-color 120ms;
}
.input-base:focus { border-color: ${T.brand}; }

.btn-base {
  height: 34px; padding: 0 14px; border-radius: 7px; border: 1px solid ${T.border};
  background: ${T.white}; color: ${T.text}; font-size: 12px; font-weight: 600;
  cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  font-family: 'Plus Jakarta Sans', sans-serif; white-space: nowrap;
  transition: all 120ms;
}
.btn-base:hover { background: ${T.bg}; border-color: #CCC9C0; }
.btn-primary {
  background: ${T.brand}; color: #fff; border-color: ${T.brand};
}
.btn-primary:hover { background: #323d47; border-color: #323d47; }
.btn-ghost { background: transparent; border-color: transparent; color: ${T.muted}; }
.btn-ghost:hover { background: ${T.bg}; border-color: ${T.border}; color: ${T.text}; }

.detail-section { }
.detail-label { font-size: 10px; color: ${T.faint}; margin-bottom: 2px; letter-spacing: 0.02em; }
.detail-value { font-size: 12px; color: ${T.text}; font-weight: 500; line-height: 1.4; word-break: break-word; }

.bar-fill { height: 3px; border-radius: 2px; transition: width 800ms cubic-bezier(.4,0,.2,1); }

@keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
.animate-in { animation: fadeIn 180ms ease forwards; }
`;

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS = {
  won:  { label: 'Won',   color: T.green, bg: T.greenBg },
  lost: { label: 'Lost',  color: T.red,   bg: T.redBg   },
  open:    { label: 'Offen',   color: T.blue,  bg: T.blueBg  },
  deleted: { label: 'Gelöscht', color: T.muted, bg: T.borderSm },
};


// Stages ordered by seriousness — everything from "Vor-Ort" onward = hot
const STAGE_HEAT = {
  // Cold / early
  'Lead-Eingang':           { heat: 0, bg: null,                  text: null    },
  'Beantragung ausstehend': { heat: 1, bg: '#FFF9F0',             text: '#92400E' },
  // Hot — from here it's serious
  'Vor-Ort Beratung':       { heat: 2, bg: '#FFFBEB',             text: '#78350F' },
  'Vor-Ort Verpflichtend':  { heat: 3, bg: '#FEF3C7',             text: '#78350F' },
  'BGS beantragt':          { heat: 4, bg: '#ECFDF5',             text: '#065F46' },
  'BGS erhalten':           { heat: 5, bg: '#D1FAE5',             text: '#065F46' },
  'Erhält Termin':          { heat: 6, bg: '#D1FAE5',             text: '#065F46' },
  'Termin beim':            { heat: 7, bg: '#A7F3D0',             text: '#064E3B' },
  'Absage erhalten':        { heat: 0, bg: '#FEE2E2',             text: '#991B1B' },
  'Zusage erhalten':        { heat: 8, bg: '#86EFAC',             text: '#14532D' },
  'Warteliste':             { heat: 7, bg: '#D1FAE5',             text: '#065F46' },
  'Kursstart':              { heat: 9, bg: '#6EE7B7',             text: '#064E3B' },
};

function getStageStyle(stageName) {
  if (!stageName) return null;
  // Partial match — stage names can be truncated
  const key = Object.keys(STAGE_HEAT).find(k => stageName.toLowerCase().includes(k.toLowerCase().split(' ')[0]) && STAGE_HEAT[k].heat >= 1);
  // Exact or prefix match
  const exact = Object.keys(STAGE_HEAT).find(k => stageName.toLowerCase().startsWith(k.toLowerCase().substring(0, 8)));
  const match = STAGE_HEAT[exact] || (key ? STAGE_HEAT[key] : null);
  return match?.bg ? match : null;
}

// ── Atom components ────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.open;
  return (
    <span className="pill" style={{ background: s.bg, color: s.color }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0, display: 'inline-block' }} />
      {s.label}
    </span>
  );
}

function ChannelBadge({ b }) {
  const ch = b.channel || b.source || '';
  const [tip, setTip] = useState(false);
  if (ch === 'tg') {
    const group = b.utm_term || null;
    const id    = b.utm_content || null;
    return (
      <span style={{ position: 'relative', display: 'inline-block' }}
        onMouseEnter={() => setTip(true)} onMouseLeave={() => setTip(false)}>
        <span className="pill" style={{ background: '#E0F2FE', color: '#0369A1', cursor: 'default' }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
          Telegram {group ? `· ${group}` : ''}
        </span>
        {tip && (group || id) && (
          <div className="tg-tip">
            {group && <div style={{ fontWeight: 700, marginBottom: 2 }}>📢 {group}</div>}
            {id    && <div className="mono" style={{ opacity: 0.6, fontSize: 10 }}>ID: {id}</div>}
          </div>
        )}
      </span>
    );
  }
  if (ch === 'fb_lead') return <span className="pill" style={{ background: '#1D4ED8', color: '#fff' }} title={b.erreichbarkeit ? `Erreichbar: ${b.erreichbarkeit}` : ''}>📞 FB-Lead</span>;
  if (ch === 'fb') return <span className="pill" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>👍 Facebook</span>;
  if (ch === 'webinar' || b.webinar_id) return <span className="pill" style={{ background: T.amberBg, color: T.amber }}>🖥 Webinar</span>;
  if (ch === 'event') return <span className="pill" style={{ background: '#FFF0F5', color: '#BE185D' }}>🎪 Event</span>;
  if (ch === 'institutionen') return <span className="pill" style={{ background: '#EDE9FE', color: '#5B21B6' }}>🏛 Institution</span>;
  return <span style={{ color: T.faint, fontSize: 12 }}>—</span>;
}

function UTMStack({ b }) {
  const tags = [
    b.utm_source    && ['src', b.utm_source],
    b.utm_medium    && ['med', b.utm_medium],
    b.utm_campaign  && ['cmp', b.utm_campaign],
    b.sub_id        && ['sub', b.sub_id],
    b.utm_content   && ['cnt', b.utm_content],
    b.utm_term      && ['trm', b.utm_term],
  ].filter(Boolean);
  if (!tags.length) return <span style={{ color: T.faint, fontSize: 12 }}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, maxWidth: 200 }}>
      {tags.map(([k, v]) => (
        <span key={k} className="utm-tag" title={`${k}: ${v}`}>
          <span className="key">{k}:</span>{v}
        </span>
      ))}
    </div>
  );
}

// ── KPI strip ──────────────────────────────────────────────────────────────────
function KPIStrip({ bookings }) {
  const won  = bookings.filter(b => b.pd?.status === 'won' || (b.pd?.stage && WON_STAGES.includes(b.pd.stage))).length;
  const lost = bookings.filter(b => b.pd?.status === 'lost' && !WON_STAGES.includes(b.pd?.stage)).length;
  const open = bookings.filter(b => b.pd?.status === 'open' && !WON_STAGES.includes(b.pd?.stage)).length;
  const deal = bookings.filter(b => b.pd?.dealId).length;
  const rate = bookings.length ? Math.round(won / bookings.length * 100) : 0;
  const noDeal = bookings.length - deal;

  const kpis = [
    { label: 'Buchungen',  v: bookings.length, color: T.text },
    { label: 'In Pipedrive', v: deal,          color: T.brand },
    { label: 'Kein Deal',  v: noDeal,          color: T.amber },
    { label: 'Gelöscht',   v: bookings.filter(b => b.pd?.status === 'deleted').length, color: T.muted },
    { label: 'Offen',      v: open,            color: T.blue  },
    { label: 'Gewonnen',   v: won,             color: T.green },
    { label: 'Lost',       v: lost,            color: T.red   },
    { label: 'Won-Rate',   v: `${rate}%`,      color: rate > 30 ? T.green : T.muted },
  ];

  return (
    <div className="card" style={{ display: 'flex', marginBottom: 20, overflow: 'hidden' }}>
      {kpis.map((k, i) => (
        <div key={k.label} style={{
          flex: 1, padding: '16px 18px',
          borderRight: i < kpis.length - 1 ? `1px solid ${T.borderSm}` : 'none',
          minWidth: 0,
        }}>
          <div className="mono" style={{ fontSize: 22, fontWeight: 500, color: k.color, lineHeight: 1 }}>
            {k.v}
          </div>
          <div style={{ fontSize: 10, color: T.muted, marginTop: 5, textTransform: 'uppercase',
            letterSpacing: '0.08em', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {k.label}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Funnel ─────────────────────────────────────────────────────────────────────
const WON_STAGES = ['Zusage erhalten', 'BGS erhalten', 'Warteliste Kursstart', 'Kursstart anstehend', 'Kursstart'];

function Funnel({ bookings }) {
  const n    = bookings.length || 1;
  const deal = bookings.filter(b => b.pd?.dealId).length;
  const open = bookings.filter(b => b.pd?.status === 'open' && !WON_STAGES.includes(b.pd?.stage)).length;
  const won  = bookings.filter(b => b.pd?.status === 'won' || (b.pd?.stage && WON_STAGES.includes(b.pd.stage))).length;
  const lost = bookings.filter(b => b.pd?.status === 'lost' && !WON_STAGES.includes(b.pd?.stage)).length;

  const deleted = bookings.filter(b => b.pd?.status === 'deleted').length;
  const unknown = bookings.filter(b => b.pd?.dealId && !b.pd?.error && !b.pd?.status && !b.pd?.status === 'deleted').length;
  const errored = bookings.filter(b => b.pd?.error).length;

  const [showDiag, setShowDiag] = React.useState(false);
  const [diagData, setDiagData] = React.useState(null);
  const [diagLoading, setDiagLoading] = React.useState(false);
  const [stagesData, setStagesData] = React.useState(null);
  const runDiag = async () => {
    setDiagLoading(true); setShowDiag(true);
    try {
      const auth = { Authorization: 'Bearer ' + sessionStorage.getItem('zba_admin_token') };
      const [diagR, stagesR] = await Promise.all([
        fetch('/api/admin/pd-diagnostics', { headers: auth }).then(r => r.json()),
        fetch('/api/admin/pd-stages', { headers: auth }).then(r => r.json()).catch(() => null),
      ]);
      setDiagData(diagR);
      if (stagesR) setStagesData(stagesR.stages);
    } catch(e) { setDiagData({ error: e.message }); }
    setDiagLoading(false);
  };

  const steps = [
    { label: 'Buchungen', n: bookings.length, bar: '#3E4954' },
    { label: 'In PD',     n: deal,            bar: '#6366F1' },
    { label: 'Offen',     n: open,            bar: '#3B82F6' },
    { label: 'Won',       n: won,             bar: '#22C55E' },
    { label: 'Lost',      n: lost,            bar: '#F43F5E' },
    ...(deleted > 0 ? [{ label: 'Gelöscht',  n: deleted, bar: '#94A3B8' }] : []),
    ...(unknown > 0 ? [{ label: 'Unbekannt', n: unknown, bar: '#A0A0A0'  }] : []),
    ...(errored > 0 ? [{ label: 'Fehler',    n: errored, bar: '#F59E0B' }] : []),
  ];

  return (
    <div style={{ flex: '0 0 auto', minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase',
        letterSpacing: '0.1em', marginBottom: 14 }}>Funnel</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0 }}>
        {steps.map((s, i) => {
          const pct = Math.round(s.n / n * 100);
          const maxH = 72;
          const h = Math.max(24, Math.round(pct / 100 * maxH));
          return (
            <React.Fragment key={s.label}>
              <div className="funnel-node">
                <div className="mono" style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 4 }}>{s.n}</div>
                <div style={{ width: '100%', height: h, background: s.bar, borderRadius: '5px 5px 0 0', opacity: 0.85 }} />
                <div style={{ width: '100%', height: 3, background: s.bar, marginBottom: 6 }} />
                <div style={{ fontSize: 9.5, color: T.muted, fontWeight: 600, textAlign: 'center', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>{s.label}</div>
                <div className="mono" style={{ fontSize: 9, color: T.faint }}>{pct}%</div>
              </div>
              {i < steps.length - 1 && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.faint} strokeWidth="2"
                  style={{ flexShrink: 0, alignSelf: 'center', marginBottom: 48, marginLeft: 2, marginRight: 2 }}>
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Unknown/error note */}
      {(unknown > 0 || errored > 0) && (
        <div style={{ marginTop: 12, fontSize: 11, color: T.muted, lineHeight: 1.5 }}>
          {deleted > 0 && <div>🗑 {deleted} Deal{deleted > 1 ? 's' : ''} in Pipedrive gelöscht (Duplikate)</div>}
          {unknown > 0 && <div>⚠ {unknown} Deal{unknown > 1 ? 's' : ''} mit unbekanntem Status</div>}
          {errored > 0 && <div>⚠ {errored} Deal{errored > 1 ? 's' : ''} konnten nicht geladen werden</div>}
          <button onClick={runDiag} style={{ marginTop: 8, padding: '6px 14px', fontSize: 11, fontWeight: 700, border: '1.5px solid #F59E0B', borderRadius: 8, background: showDiag ? '#FEF3C7' : '#fff', color: '#92400E', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
            {diagLoading ? 'Analysiere...' : '🔍 PD Fehler analysieren'}
          </button>
        </div>
      )}

      {/* PD Diagnostik Panel */}
      {showDiag && diagData && !diagData.error && (
        <div style={{ marginTop: 16, padding: '16px 20px', background: '#fff', borderRadius: 12, border: '1px solid #e2e6ea' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e2b38', margin: 0 }}>Pipedrive Diagnose</h3>
            <button onClick={() => setShowDiag(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: '#8a95a3' }}>✕</button>
          </div>

          {/* Summary badges */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            {Object.entries(diagData.summary || {}).map(([k, v]) => {
              const colors = { ok: { bg: '#DCFCE7', c: '#166534' }, no_deal: { bg: '#FEF3C7', c: '#92400E' }, deal_not_found: { bg: '#FFE4E6', c: '#9F1239' }, deal_deleted: { bg: '#F3E8FF', c: '#6B21A8' }, api_error: { bg: '#FEE2E2', c: '#991B1B' } };
              const col = colors[k] || { bg: '#F3F4F6', c: '#374151' };
              const labels = { ok: 'OK', no_deal: 'Kein Deal', deal_not_found: 'Deal nicht gefunden', deal_deleted: 'Deal geloescht', api_error: 'API Fehler' };
              return <span key={k} style={{ padding: '4px 12px', borderRadius: 100, fontSize: 12, fontWeight: 700, background: col.bg, color: col.c }}>{labels[k] || k}: {v}</span>;
            })}
          </div>

          {/* Error list */}
          {(diagData.results || []).filter(r => r.pd_status !== 'ok').length > 0 && (
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e6ea', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px', color: '#8a95a3', fontWeight: 700 }}>Ref</th>
                    <th style={{ padding: '8px 10px', color: '#8a95a3', fontWeight: 700 }}>Name</th>
                    <th style={{ padding: '8px 10px', color: '#8a95a3', fontWeight: 700 }}>E-Mail</th>
                    <th style={{ padding: '8px 10px', color: '#8a95a3', fontWeight: 700 }}>Quelle</th>
                    <th style={{ padding: '8px 10px', color: '#8a95a3', fontWeight: 700 }}>Datum</th>
                    <th style={{ padding: '8px 10px', color: '#8a95a3', fontWeight: 700 }}>Status</th>
                    <th style={{ padding: '8px 10px', color: '#8a95a3', fontWeight: 700 }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {(diagData.results || []).filter(r => r.pd_status !== 'ok').map((r, i) => {
                    const statusColors = { no_deal: '#F59E0B', deal_not_found: '#EF4444', deal_deleted: '#8B5CF6', api_error: '#DC2626' };
                    const statusLabels = { no_deal: 'Kein Deal', deal_not_found: 'Nicht gefunden', deal_deleted: 'Geloescht', api_error: 'API Fehler' };
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f2f5' }}>
                        <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11 }}>{r.booking_ref}</td>
                        <td style={{ padding: '8px 10px' }}>{r.vorname} {r.nachname}</td>
                        <td style={{ padding: '8px 10px', color: '#6b7a8d' }}>{r.email}</td>
                        <td style={{ padding: '8px 10px' }}>{r.source || '-'}</td>
                        <td style={{ padding: '8px 10px', color: '#6b7a8d' }}>{r.created ? new Date(r.created).toLocaleDateString('de-DE') : '-'}</td>
                        <td style={{ padding: '8px 10px' }}><span style={{ color: statusColors[r.pd_status] || '#6b7a8d', fontWeight: 600 }}>{statusLabels[r.pd_status] || r.pd_status}</span></td>
                        <td style={{ padding: '8px 10px', fontSize: 10, color: '#8a95a3', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={JSON.stringify(r.pd_detail)}>{r.pd_detail?.error || r.pd_detail?.types?.join(', ') || (r.pd_detail?.dealId ? 'Deal #'+r.pd_detail.dealId : '-')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {(diagData.results || []).filter(r => r.pd_status !== 'ok').length === 0 && (
            <div style={{ textAlign: 'center', padding: 20, color: '#16a34a', fontWeight: 600 }}>✅ Alle Deals korrekt verknuepft</div>
          )}

          {/* Pipeline Stages */}
          {stagesData && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e6ea' }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e2b38', marginBottom: 10 }}>Pipeline Stages</h4>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {stagesData.map(s => {
                  const isWon = WON_STAGES.includes(s.name);
                  return (
                    <span key={s.id} style={{ padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                      background: isWon ? '#DCFCE7' : '#F3F4F6', color: isWon ? '#166534' : '#374151',
                      border: `1px solid ${isWon ? '#86EFAC' : '#E5E7EB'}` }}>
                      {isWon ? '✓ ' : ''}{s.name} <span style={{ opacity: 0.5 }}>#{s.id}</span>
                    </span>
                  );
                })}
              </div>
              <div style={{ fontSize: 10, color: '#8a95a3', marginTop: 8 }}>Gruen markierte Stages zaehlen als "Gewonnen" im Funnel.</div>
            </div>
          )}
        </div>
      )}

      {/* Conversion rates */}
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.borderSm}` }}>
        {[
          ['Buchung → Deal', deal, bookings.length, '#6366F1'],
          ['Deal → Won',     won,  deal,            '#22C55E'],
          ['Deal → Lost',    lost, deal,            '#F43F5E'],
        ].map(([label, a, b, color]) => {
          const pct = b > 0 ? Math.round(a / b * 100) : 0;
          return (
            <div key={label} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: T.muted }}>{label}</span>
                <span className="mono" style={{ fontSize: 11, fontWeight: 500, color: T.text }}>{pct}%</span>
              </div>
              <div style={{ height: 3, background: T.borderSm, borderRadius: 2 }}>
                <div className="bar-fill" style={{ width: `${pct}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── UTM Breakdown ──────────────────────────────────────────────────────────────
function UTMBreakdown({ bookings }) {
  const tally = key => {
    const m = {};
    bookings.forEach(b => { const v = b[key]; if (v) m[v] = (m[v]||0)+1; });
    return Object.entries(m).sort((a,b)=>b[1]-a[1]).slice(0,7);
  };

  const cols = [
    ['Kanal',        tally('channel')],
    ['UTM Source',   tally('utm_source')],
    ['UTM Medium',   tally('utm_medium')],
    ['UTM Campaign', tally('utm_campaign')],
    ['Sub-ID',       tally('sub_id')],
  ].filter(([,e]) => e.length);

  if (!cols.length) return (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>Kanäle & UTM</div>
      <div style={{ color: T.faint, fontSize: 13 }}>Noch keine Tracking-Daten.</div>
    </div>
  );

  const globalMax = Math.max(...cols.flatMap(([,e])=>e.map(([,n])=>n)), 1);

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase',
        letterSpacing: '0.1em', marginBottom: 14 }}>Kanäle & UTM</div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {cols.map(([title, entries]) => (
          <div key={title} style={{ flex: '1 1 100px', minWidth: 90 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: T.faint, textTransform: 'uppercase',
              letterSpacing: '0.1em', marginBottom: 8, borderBottom: `1px solid ${T.borderSm}`, paddingBottom: 4 }}>
              {title}
            </div>
            {entries.map(([k, n]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                    <span style={{ fontSize: 11, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap', maxWidth: 90 }} title={k}>{k}</span>
                    <span className="mono" style={{ fontSize: 11, color: T.muted, flexShrink: 0, marginLeft: 4 }}>{n}</span>
                  </div>
                  <div style={{ height: 2, background: T.borderSm, borderRadius: 1 }}>
                    <div style={{ height: '100%', borderRadius: 1, background: T.brand, opacity: 0.6,
                      width: `${Math.round(n/globalMax*100)}%`, transition: 'width 600ms ease' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Expanded detail ────────────────────────────────────────────────────────────
function InfoPair({ label, value, mono }) {
  if (value == null || value === '' || value === false) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="detail-label">{label}</div>
      <div className={`detail-value${mono ? ' mono' : ''}`} style={mono ? { fontSize: 11 } : {}}>
        {value === true ? '✓' : value}
      </div>
    </div>
  );
}

function ExpandedDetail({ b }) {
  const pd = b.pd;
  return (
    <tr>
      <td colSpan={9} style={{ padding: '0 14px 20px 44px', background: 'rgba(62,73,84,0.025)', borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 24, paddingTop: 16 }}>

          {pd && !pd.error && (
            <div className="detail-section">
              <div style={{ fontSize: 10, fontWeight: 700, color: T.brand, textTransform: 'uppercase',
                letterSpacing: '0.1em', marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${T.borderSm}` }}>
                Pipedrive
              </div>
              <InfoPair label="Status"       value={<StatusPill status={pd.status} />} />
              <InfoPair label="Stage"        value={pd.stage} />
              <InfoPair label="Owner"        value={pd.ownerName} />
              <InfoPair label="Person"       value={pd.personName} />
              <InfoPair label="Erstellt"     value={pd.addTime?.substring(0,10)} />
              <InfoPair label="Won am"       value={pd.wonTime?.substring(0,10)} />
              <InfoPair label="Lost am"      value={pd.lostTime?.substring(0,10)} />
              <InfoPair label="Lost-Grund"   value={pd.lostReason} />
              <InfoPair label="Nächste Akt." value={pd.nextActivityDate && `${pd.nextActivity} · ${pd.nextActivityDate}`} />
              <InfoPair label="Akt. / Notizen" value={`${pd.activitiesCount || 0} / ${pd.notesCount || 0}`} />
            </div>
          )}
          {pd?.error && (
            <div className="detail-section">
              <div style={{ fontSize: 10, fontWeight: 700, color: T.red, textTransform: 'uppercase',
                letterSpacing: '0.1em', marginBottom: 8 }}>Pipedrive</div>
              <span style={{ fontSize: 12, color: T.red }}>Fehler beim Laden des Deals</span>
            </div>
          )}

          <div className="detail-section">
            <div style={{ fontSize: 10, fontWeight: 700, color: T.brand, textTransform: 'uppercase',
              letterSpacing: '0.1em', marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${T.borderSm}` }}>
              Tracking
            </div>
            <InfoPair label="Kanal"         value={b.channel || b.source} />
            {b.status === 'lead' && (<>
              <InfoPair label="Telefon"       value={b.telefon} mono />
              <InfoPair label="Erreichbar"    value={b.erreichbarkeit} />
              <InfoPair label="Arbeitssuchend" value={b.arbeitslos} />
              <InfoPair label="Führerschein B" value={b.fuehrerschein_b} />
              <InfoPair label="Meta Event-ID" value={b.meta_event_id} mono />
              <InfoPair label="fbclid"        value={b.fbclid ? 'ja' : 'nein'} />
            </>)}
            <InfoPair label="Sprache"       value={b.language?.toUpperCase()} />
            <InfoPair label="UTM Source"    value={b.utm_source}   mono />
            <InfoPair label="UTM Medium"    value={b.utm_medium}   mono />
            <InfoPair label="UTM Campaign"  value={b.utm_campaign} mono />
            <InfoPair label="UTM Content"   value={b.utm_content}  mono />
            <InfoPair label="UTM Term"      value={b.utm_term}     mono />
            <InfoPair label="Sub-ID"        value={b.sub_id}       mono />
            <InfoPair label="Lead-Source"   value={b.lead_source} />
            <InfoPair label="Webinar Datum" value={b.webinar_date} />
            <InfoPair label="Webinar-ID"    value={b.webinar_id}  mono />
            <InfoPair label="Referrer"      value={b.referrer_url} />
            <InfoPair label="Lead-IP"       value={b.lead_ip}     mono />
          </div>

          <div className="detail-section">
            <div style={{ fontSize: 10, fontWeight: 700, color: T.brand, textTransform: 'uppercase',
              letterSpacing: '0.1em', marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${T.borderSm}` }}>
              Kontakt
            </div>
            <InfoPair label="Telefon"   value={b.telefon} />
            <InfoPair label="E-Mail"    value={b.email} />
            <InfoPair label="Wohnort"   value={b.ort} />
            <InfoPair label="Berater"   value={b.berater_name} />
            <InfoPair label="Art"       value={b.type === 'onsite' ? '🏢 Vor-Ort' : '📞 Telefon'} />
            <InfoPair label="Gebucht"   value={b.created_at?.substring(0,16)?.replace('T',' ')} />
          </div>

          {(b.deutschkenntnisse || b.fuehrerschein_b) && (
            <div className="detail-section">
              <div style={{ fontSize: 10, fontWeight: 700, color: T.brand, textTransform: 'uppercase',
                letterSpacing: '0.1em', marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${T.borderSm}` }}>
                Qualifikation
              </div>
              <InfoPair label="Deutschkenntnisse"
                value={b.deutschkenntnisse === 'ja' ? '✅ Ausreichend' : b.deutschkenntnisse === 'nein' ? '❌ Nicht ausreichend' : b.deutschkenntnisse} />
              <InfoPair label="Führerschein Kl. B"
                value={b.fuehrerschein_b === 'ja' ? '✅ Vorhanden' : b.fuehrerschein_b === 'nein' ? '❌ Nicht vorhanden' : b.fuehrerschein_b} />
            </div>
          )}
        </div>

        {pd?.dealId && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.borderSm}` }}>
            <PipedriveTimeline dealId={pd.dealId} dealUrl={pd.dealUrl} />
          </div>
        )}
      </td>
    </tr>
  );
}

// ── Login ──────────────────────────────────────────────────────────────────────
function Login({ onAuth }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const submit = async e => {
    e.preventDefault();
    const r = await adminLogin(pw);
    if (r.success) onAuth();
    else setErr('Falsches Passwort');
  };
  return (
    <div className="crm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <div className="card" style={{ padding: '36px', width: 320 }}>
        <img src="https://zba.academy/webinar/images/zbalogo2.png"
          style={{ height: 28, marginBottom: 24 }} alt="ZBA" />
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: T.text }}>CRM & Deals</div>
        <form onSubmit={submit}>
          <input className="input-base" type="password" value={pw} onChange={e => setPw(e.target.value)}
            placeholder="Passwort" autoFocus style={{ width: '100%', marginBottom: 10 }} />
          {err && <div style={{ fontSize: 12, color: T.red, marginBottom: 10 }}>{err}</div>}
          <button type="submit" className="btn-base btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
            Einloggen
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Main dashboard ─────────────────────────────────────────────────────────────
export default function CRMDashboard() {
  const [auth, setAuth]               = useState(isAuthenticated());
  const [bookings, setBookings]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [expanded, setExpanded]       = useState(null);
  const [search, setSearch]           = useState('');
  const [fStatus, setFStatus]         = useState('');
  const [fChannel, setFChannel]       = useState('');
  const [updated, setUpdated]         = useState(null);
  const [showCSV, setShowCSV]         = useState(false);
  const [onlySubId, setOnlySubId]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/crm', {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('zba_admin_token')}` }
      });
      const d = await res.json();
      setBookings(d.bookings || []);
      setUpdated(new Date().toLocaleTimeString('de-DE'));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (auth) load(); }, [auth]);

  if (!auth) return (
    <>
      <style>{GLOBAL_CSS}</style>
      <Login onAuth={() => setAuth(true)} />
    </>
  );

  const filtered = bookings.filter(b => {
    const q = search.toLowerCase();
    const ms = !q || [b.vorname, b.nachname, b.email, b.booking_ref,
      b.utm_source, b.utm_campaign, b.utm_term, b.sub_id, b.pd?.stage]
      .some(v => v?.toLowerCase().includes(q));
    const mst = !fStatus  || b.pd?.status === fStatus || (!b.pd?.dealId && fStatus === 'no_deal');
    const mch = !fChannel || (b.channel || b.source) === fChannel;
    const msub = !onlySubId || !!b.sub_id;
    return ms && mst && mch && msub;
  });

  return (
    <div className="crm">
      <style>{GLOBAL_CSS}</style>

      {/* ── Header ── */}
      <div style={{ background: T.white, borderBottom: `1px solid ${T.border}`, height: 48,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', position: 'sticky', top: 0, zIndex: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <img src="https://zba.academy/webinar/images/zbalogo2.png"
            style={{ height: 22 }} alt="ZBA" />
          <span style={{ width: 1, height: 18, background: T.borderSm, display: 'block' }} />
          <span className="syne" style={{ fontSize: 13, fontWeight: 700, color: T.text, letterSpacing: '-0.01em' }}>
            CRM & Deals
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {updated && <span style={{ fontSize: 11, color: T.faint }}>Stand {updated}</span>}
          <button className="btn-base btn-ghost" onClick={() => setShowCSV(!showCSV)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
            CSV Import
          </button>
          <button className="btn-base btn-primary" onClick={load} disabled={loading}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}>
              <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            {loading ? 'Lädt…' : 'Aktualisieren'}
          </button>
          <a href="/admin/leads" className="btn-base btn-ghost">📞 FB-Leads</a>
          <a href="/admin/bookings" className="btn-base btn-ghost">📋 Buchungen</a>
          <a href="/admin/step2work" className="btn-base btn-ghost">🤝 Step2Work</a>
          <a href="/events-admin" className="btn-base btn-ghost">🎪 Events</a>
          <a href="/settings" className="btn-base btn-ghost">⚙️ Settings</a>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth: 1600, margin: '0 auto', padding: '24px 24px' }}>

        {loading && !bookings.length ? (
          <div style={{ textAlign: 'center', padding: 100, color: T.faint }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>↻</div>
            Lade Pipedrive-Daten… (kann 10–30 Sek. dauern)
          </div>
        ) : (
          <>
            {/* CSV Import — hidden by default */}
            {showCSV && (
              <div className="card animate-in" style={{ marginBottom: 16, overflow: 'hidden' }}>
                <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.borderSm}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>CSV Import — Webinar-Leads</span>
                  <button className="btn-base btn-ghost" style={{ height: 28 }} onClick={() => setShowCSV(false)}>✕</button>
                </div>
                <div style={{ padding: '16px 24px' }}>
                  <CSVImport onImported={() => { load(); setShowCSV(false); }} />
                </div>
              </div>
            )}

            {/* KPI strip */}
            <KPIStrip bookings={bookings} />

            {/* Funnel + UTM side by side */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 0 }}>
                <div style={{ padding: '20px 24px', borderRight: `1px solid ${T.borderSm}`, flexShrink: 0, minWidth: 340 }}>
                  <Funnel bookings={bookings} />
                </div>
                <div style={{ padding: '20px 24px', flex: 1, minWidth: 0 }}>
                  <UTMBreakdown bookings={bookings} />
                </div>
              </div>
            </div>

            {/* Filter row */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2"
                  style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input className="input-base" type="text" value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Name, E-Mail, Ref, UTM, Sub-ID…"
                  style={{ width: '100%', paddingLeft: 32 }} />
              </div>
              <select className="input-base" value={fStatus} onChange={e => setFStatus(e.target.value)}>
                <option value="">Alle Status</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
                <option value="open">Offen</option>
                <option value="no_deal">Kein Deal</option>
              </select>
              <select className="input-base" value={fChannel} onChange={e => setFChannel(e.target.value)}>
                <option value="">Alle Kanäle</option>
                <option value="fb">Facebook (Termin)</option>
                <option value="fb_lead">Facebook-Lead (Rückruf)</option>
                <option value="tg">Telegram</option>
                <option value="webinar">Webinar</option>
              </select>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                fontSize: 12, color: onlySubId ? T.brand : T.muted, fontWeight: onlySubId ? 600 : 400,
                padding: '0 4px', userSelect: 'none', whiteSpace: 'nowrap' }}>
                <div onClick={() => setOnlySubId(!onlySubId)} style={{
                  width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                  border: `1.5px solid ${onlySubId ? T.brand : T.border}`,
                  background: onlySubId ? T.brand : T.white,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 120ms',
                }}>
                  {onlySubId && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>}
                </div>
                <span onClick={() => setOnlySubId(!onlySubId)}>Nur mit Sub-ID</span>
              </label>
              <span className="mono" style={{ fontSize: 11, color: T.faint }}>
                {filtered.length}/{bookings.length}
              </span>
            </div>

            {/* Table */}
            <div className="card" style={{ overflow: 'auto' }}>
              <table className="crm-table">
                <thead>
                  <tr>
                    {['', 'Ref', 'Name', 'Termin', 'Kanal', 'UTM + Sub-ID', 'Status', 'Stage', 'Deal'].map(h => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!filtered.length ? (
                    <tr><td colSpan={9} style={{ padding: 60, textAlign: 'center', color: T.faint }}>
                      Keine Buchungen gefunden.
                    </td></tr>
                  ) : filtered.map(b => {
                    const pd  = b.pd;
                    const exp = expanded === b.booking_ref;
                    return (
                      <React.Fragment key={b.booking_ref}>
                        <tr className={exp ? 'expanded' : ''} onClick={() => setExpanded(exp ? null : b.booking_ref)}>
                          {/* Expand indicator */}
                          <td style={{ width: 20, paddingRight: 4 }}>
                            <span style={{ fontSize: 9, color: T.faint, display: 'block', transition: 'transform 150ms',
                              transform: exp ? 'rotate(90deg)' : 'none' }}>▶</span>
                          </td>
                          {/* Ref */}
                          <td>
                            <span className="mono" style={{ fontSize: 10, color: T.muted }}>{b.booking_ref}</span>
                          </td>
                          {/* Name + email */}
                          <td>
                            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.2 }}>
                              {b.vorname} {b.nachname}
                            </div>
                            <div style={{ fontSize: 11, color: T.faint, marginTop: 2 }}>{b.email}</div>
                          </td>
                          {/* Termin */}
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <div className="mono" style={{ fontSize: 12, color: T.text }}>{b.date}</div>
                            <div className="mono" style={{ fontSize: 11, color: T.muted }}>{b.time}</div>
                          </td>
                          {/* Kanal */}
                          <td><ChannelBadge b={b} /></td>
                          {/* UTM */}
                          <td><UTMStack b={b} /></td>
                          {/* Status */}
                          <td>
                            {pd
                              ? (pd.error ? <span style={{ fontSize: 11, color: T.amber }}>⚠ Fehler</span> : WON_STAGES.includes(pd.stage) ? <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, background: T.greenBg, color: T.green, fontWeight: 600 }}>{pd.stage}</span> : <StatusPill status={pd.status} />)
                              : <span style={{ fontSize: 11, color: T.faint }}>Kein Deal</span>}
                          </td>
                          {/* Stage */}
                          <td style={{ fontSize: 11, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            fontWeight: getStageStyle(pd?.stage) ? 700 : 400,
                            color: getStageStyle(pd?.stage)?.text || T.muted }}>
                            {pd?.stage || '—'}
                          </td>
                          {/* Deal */}
                          <td>
                            {pd?.dealUrl && (
                              <a href={pd.dealUrl} target="_blank" rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                style={{ fontSize: 11, color: T.brand, fontWeight: 600, textDecoration: 'underline',
                                  textDecorationColor: T.brandLt, fontFamily: 'JetBrains Mono, monospace' }}>
                                #{pd.dealId}
                              </a>
                            )}
                          </td>
                        </tr>
                        {exp && <ExpandedDetail b={b} />}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
